
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { Plus, Edit2, Trash2, ExternalLink, GripVertical } from 'lucide-react';
import ClientLogoForm from './ClientLogoForm';

interface ClientLogo {
  id: string;
  name: string;
  logo_url: string;
  website_url: string | null;
  display_order: number;
  is_active: boolean;
}

const ClientLogosManager = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingLogo, setEditingLogo] = useState<ClientLogo | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: logos = [], isLoading } = useQuery({
    queryKey: ['admin-client-logos'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('client_logos')
        .select('*')
        .order('display_order', { ascending: true });
      
      if (error) throw error;
      return data as ClientLogo[];
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('client_logos')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-client-logos'] });
      queryClient.invalidateQueries({ queryKey: ['client-logos'] });
      toast({
        title: "Success",
        description: "Client logo deleted successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to delete client logo",
        variant: "destructive",
      });
      console.error('Delete error:', error);
    }
  });

  const toggleActiveMutation = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { error } = await supabase
        .from('client_logos')
        .update({ is_active })
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-client-logos'] });
      queryClient.invalidateQueries({ queryKey: ['client-logos'] });
      toast({
        title: "Success",
        description: "Client logo status updated",
      });
    },
  });

  const handleEdit = (logo: ClientLogo) => {
    setEditingLogo(logo);
    setIsFormOpen(true);
  };

  const handleDelete = (id: string, name: string) => {
    if (window.confirm(`Are you sure you want to delete "${name}"?`)) {
      deleteMutation.mutate(id);
    }
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingLogo(null);
  };

  if (isLoading) {
    return <div>Loading client logos...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Client Logos</h2>
          <p className="text-muted-foreground">Manage client logos displayed in the carousel</p>
        </div>
        <Button onClick={() => setIsFormOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Add Client Logo
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {logos.map((logo) => (
          <Card key={logo.id} className={`transition-opacity ${!logo.is_active ? 'opacity-50' : ''}`}>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-2">
                  <GripVertical className="w-4 h-4 text-gray-400" />
                  <div>
                    <CardTitle className="text-lg">{logo.name}</CardTitle>
                    <CardDescription>
                      Order: {logo.display_order}
                      {!logo.is_active && ' (Inactive)'}
                    </CardDescription>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="aspect-video bg-gray-50 rounded-lg flex items-center justify-center p-4">
                <img
                  src={logo.logo_url}
                  alt={logo.name}
                  className="max-w-full max-h-full object-contain"
                />
              </div>
              
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleEdit(logo)}
                >
                  <Edit2 className="w-4 h-4 mr-1" />
                  Edit
                </Button>
                
                {logo.website_url && (
                  <Button
                    variant="outline"
                    size="sm"
                    asChild
                  >
                    <a href={logo.website_url} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="w-4 h-4 mr-1" />
                      Visit
                    </a>
                  </Button>
                )}
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => toggleActiveMutation.mutate({ 
                    id: logo.id, 
                    is_active: !logo.is_active 
                  })}
                >
                  {logo.is_active ? 'Deactivate' : 'Activate'}
                </Button>
                
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => handleDelete(logo.id, logo.name)}
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {logos.length === 0 && (
        <Card>
          <CardContent className="text-center py-8">
            <p className="text-muted-foreground mb-4">No client logos found</p>
            <Button onClick={() => setIsFormOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Client Logo
            </Button>
          </CardContent>
        </Card>
      )}

      {isFormOpen && (
        <ClientLogoForm
          logo={editingLogo}
          onClose={handleFormClose}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['admin-client-logos'] });
            queryClient.invalidateQueries({ queryKey: ['client-logos'] });
            handleFormClose();
          }}
        />
      )}
    </div>
  );
};

export default ClientLogosManager;
